import {Button, Space} from 'antd';
import {
  ChargeStatus,
  SearchableChargeFilterInput,
  useChargesQuery,
  useOnUpdateAnalyticsSubscription,
  useStoresQuery
} from 'api/generated';
import {ButtonLink, TableCard} from 'components';
import {FilterList} from 'components/FilterList';
import deepEqual from 'deep-equal';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {PAYMENT_EXTRA_FILTERS, PAYMENT_FILTERS} from 'lib/enums';
import {omitEmpty} from 'lib/omitEmpty';
import {isDefined, sortObjectKeysByValue} from 'lib/utils';
import moment from 'moment';
import React, {useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {QueryParamConfig} from 'serialize-query-params/lib/types';
import {useDebouncedCallback} from 'use-debounce';
import {
  DelimitedArrayParam,
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams
} from 'use-query-params';
import {useUserContext} from '../../hooks/useUserContext';
import {ExportPayments} from './ExportPayments';
import {FilterQueryParams, PaymentFilters} from './PaymentFilters';
import {PaymentsTable} from './PaymentsTable';

const tabList = [
  {
    key: 'succeeded',
    tab: 'Succeeded'
  },
  {
    key: 'refunded',
    tab: 'Refunded'
  },
  {
    key: 'authorized',
    tab: 'Uncaptured'
  },
  {
    key: 'paid_out',
    tab: 'Paid out'
  },
  {
    key: 'failed',
    tab: 'Failed'
  },
  {
    key: 'all',
    tab: 'All'
  }
];

export const queryFiltersSchema = {
  accountId: StringParam,
  acquirer: StringParam,
  processor: StringParam,
  customerEmail: StringParam,
  customerName: StringParam,
  customerPhone: StringParam,
  currency: StringParam,
  id: StringParam,
  authorizationCode: StringParam,
  orderId: StringParam,
  providerReferenceId: StringParam,
  paymentMethodCardLast4: StringParam,
  paymentMethodCardBrand: StringParam,
  paymentMethodMethod: StringParam,
  paymentMethodBizumIntegrationType: StringParam,
  paymentMethodCardTokenizationMethod: StringParam,
  subscriptionId: StringParam,
  sequenceId: StringParam,
  sessionIp: StringParam,
  shopName: StringParam,
  shopCountry: StringParam,
  createdAt: DelimitedArrayParam,
  traceDetailsSource: StringParam,
  masterAccountId: StringParam,
  billingPlan: StringParam,
  amount: NumberParam,
  amountOperator: StringParam,
  storeId: StringParam
};

const textFields = ['shopName', 'customerEmail', 'customerName', 'storeId'];
const excludeKeys = ['amountOperator'];
const booleanFields: string[] = [];

const buildFilter = (
  queryFilters: {[key: string]: any},
  status: string,
  accountId?: string,
  isMasterAccount?: boolean
): SearchableChargeFilterInput => {
  const filter: SearchableChargeFilterInput = {};
  if (isMasterAccount) {
    filter.masterAccountId = {match: accountId};
  }
  if (accountId && !isMasterAccount) {
    filter.accountId = {match: accountId};
  }
  if (status === 'refunded') {
    filter.or = [
      {status: {match: ChargeStatus.REFUNDED}},
      {status: {match: ChargeStatus.PARTIALLY_REFUNDED}}
    ];
  } else if (status && status !== 'all') {
    filter.status = {match: status.toUpperCase()};
  }
  Object.entries(queryFilters)
    .filter(([key]) => !excludeKeys.includes(key))
    .forEach(([key, value]) => {
      if (key === 'createdAt') {
        const range = value || [];
        filter.createdAt = {
          range: [moment(range[0]).startOf('day').unix(), moment(range[1]).endOf('day').unix()]
        };
        return;
      }
      if (!isDefined(value, {allowEmptyString: false})) return;
      if (key === 'amount') {
        const operator = queryFilters.amountOperator || 'eq';
        (filter as any)[key] = {[operator]: value};
      } else {
        const queryType = textFields.includes(key)
          ? 'matchPhrase'
          : booleanFields.includes(key)
          ? 'exists'
          : 'match';
        (filter as any)[key] = {[queryType]: value};
      }
    });
  return filter;
};

export const Payments: React.FC<{accountId: string; isMasterAccount?: boolean}> = ({
  accountId,
  isMasterAccount = false
}) => {
  const history = useHistory();
  const {isMaster, isAdmin} = useAccessLevel();
  const {storeId} = useUserContext();
  const [size = 20, setSize] = useQueryParam<number | undefined>(
    'size',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [from = 0, setFrom] = useQueryParam<number | undefined>(
    'from',
    NumberParam as QueryParamConfig<number | undefined>
  );
  const [status = 'all', setStatus] = useQueryParam<string | undefined>(
    'status',
    StringParam as QueryParamConfig<string | undefined>
  );

  const [queryFilters, setQueryFilters] = useQueryParams<{[key: string]: any}>(queryFiltersSchema);

  const filter = buildFilter(queryFilters, status, accountId, isMasterAccount);
  const {data, loading, refetch, previousData} = useChargesQuery({
    variables: {from, size, filter}
  });

  const {data: storesData} = useStoresQuery({
    skip: !accountId || (!!storeId && !isAdmin),
    variables: {
      accountId
    }
  });
  const stores = storesData?.stores.items || [];

  const update = useDebouncedCallback(refetch, 5000);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useOnUpdateAnalyticsSubscription({
    onSubscriptionData: () => update()
  });

  const payload = data ?? previousData;
  const charges = payload?.charges.items || [];
  const total = payload?.charges.total || undefined;

  const handleStatusChange = (status: string) => {
    setFrom(undefined, 'pushIn');
    if (status === 'all') {
      return setStatus(undefined, 'pushIn');
    }
    setStatus(status, 'pushIn');
  };

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) {
      return refetch();
    }
    setFrom(undefined, 'pushIn');
    setQueryFilters(values, 'pushIn');
  };

  const handleCloseTag = (key: string) => setQueryFilters({...queryFilters, [key]: null}, 'pushIn');

  let availableFilters: Record<string, string> = {...PAYMENT_FILTERS};

  // Remove filters that are not available for the current user
  if (accountId && !isMasterAccount) {
    availableFilters = {...availableFilters, accountId: 'Account ID'};
  } else {
    let keysToRemove = ['storeId'];
    if (isMasterAccount) {
      keysToRemove.push('masterAccountId');
    }
    availableFilters = Object.keys(availableFilters).reduce((obj: any, key: string) => {
      if (!keysToRemove.includes(key)) {
        obj[key] = availableFilters[key];
      }
      return obj;
    }, {});
  }

  availableFilters = sortObjectKeysByValue(availableFilters);

  return (
    <>
      <PaymentFilters
        onChange={handleFiltersChange}
        queryFilters={queryFilters}
        availableFilters={availableFilters}
        accountId={accountId}
        stores={stores}
      />
      <FilterList
        queryFilters={queryFilters}
        filters={{...availableFilters, ...PAYMENT_EXTRA_FILTERS}}
        onClose={handleCloseTag}
        defaultQuery="?tab=payments"
        accountId={accountId}
      />
      <TableCard
        tabBarExtraContent={
          isMaster && (
            <Space>
              {!isMasterAccount && (
                <ButtonLink
                  type="link"
                  to={accountId ? `/payment-events?accountId=${accountId}` : `/payment-events`}>
                  Events history
                </ButtonLink>
              )}
              <ExportPayments filter={filter} />
              {accountId && !isMasterAccount && (
                <Button
                  type="primary"
                  onClick={() => history.push('/payments/new?accountId=' + accountId)}>
                  Pay By Link
                </Button>
              )}
            </Space>
          )
        }
        size="small"
        tabList={tabList}
        activeTabKey={status}
        onTabChange={handleStatusChange}>
        <PaymentsTable
          payments={charges}
          loading={loading}
          accountId={accountId}
          pagination={{
            pageSize: size,
            current: from / size + 1,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total.toLocaleString()} payments`,
            onChange: (page, size = 0) => setFrom((page - 1) * size, 'pushIn'),
            onShowSizeChange: (page, size) => setSize(size, 'pushIn')
          }}
        />
      </TableCard>
    </>
  );
};
